/**
 * DeliveryProgressBar Component - Shows delivery progress for an order
 */

import React from "react";
import { Progress, Typography, Space, Tag } from "antd";
import { CheckCircleOutlined, ClockCircleOutlined } from "@ant-design/icons";
import styled from "styled-components";

const { Text } = Typography;

const ProgressContainer = styled.div`
  margin: 16px 0;
`;

const ProgressHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const DropList = styled.div`
  margin-top: 12px;
`;

const DropItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  background: #f5f5f5;
  border-radius: 4px;
  margin-bottom: 4px;
`;

const DeliveryProgressBar = ({ order }) => {
  if (!order) return null;

  const progress = order.deliveryProgress || {};
  const deliveryDrops = order.deliveryDrops || [];

  // If no delivery drops, show simple status
  if (deliveryDrops.length === 0) {
    return null;
  }

  const completedDrops = deliveryDrops.filter(
    (drop) => drop.status === "completed"
  ).length;
  const totalDrops = deliveryDrops.length;

  return (
    <ProgressContainer>
      <ProgressHeader>
        <Space>
          <Text strong>Delivery Progress</Text>
          <Tag color={completedDrops === totalDrops ? "green" : "blue"}>
            {completedDrops} / {totalDrops} drops completed
          </Tag>
        </Space>
        {progress.progressPercentage !== undefined && (
          <Text type="secondary">
            {progress.progressPercentage}% complete
          </Text>
        )}
      </ProgressHeader>

      {progress.progressPercentage !== undefined && (
        <Progress
          percent={progress.progressPercentage}
          status={
            completedDrops === totalDrops
              ? "success"
              : completedDrops > 0
              ? "active"
              : "normal"
          }
          strokeColor={
            completedDrops === totalDrops
              ? "#52c41a"
              : completedDrops > 0
              ? "#1890ff"
              : "#d9d9d9"
          }
        />
      )}

      {deliveryDrops.length > 0 && (
        <DropList>
          {deliveryDrops.map((drop, index) => (
            <DropItem key={index}>
              {drop.status === "completed" ? (
                <CheckCircleOutlined style={{ color: "#52c41a" }} />
              ) : (
                <ClockCircleOutlined style={{ color: "#d9d9d9" }} />
              )}
              <Text
                style={{
                  flex: 1,
                  textDecoration:
                    drop.status === "completed" ? "line-through" : "none",
                  opacity: drop.status === "completed" ? 0.6 : 1,
                }}
              >
                {drop.location}
              </Text>
              <Tag
                color={
                  drop.status === "completed"
                    ? "green"
                    : drop.status === "arrived"
                    ? "blue"
                    : "default"
                }
              >
                {drop.status}
              </Tag>
              {drop.itemsDelivered > 0 && (
                <Text type="secondary" style={{ fontSize: "11px" }}>
                  {drop.itemsDelivered} items
                </Text>
              )}
            </DropItem>
          ))}
        </DropList>
      )}
    </ProgressContainer>
  );
};

export default DeliveryProgressBar;

